/*
 * Application-wide alert, confirmation and prompt dialog presenter.
 * AccountLayers mounts it for the shared dialogs service.
 * Presents one queued decision with native focus handling while the service retains its result.
 * Example: registerComponents(registry);
 *          <DialogLayer context={context} controller={dialogs}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { bindControl } from '../../runtime/components/jsx-props.js';
import { servicesContext } from '../../runtime/services.js';

export class DialogLayer extends UIElement {
  static properties = { controller: { attribute: false }, request: { state: true } };

  constructor() {
    super();
    this.request = null;
    this.captureCard = (node) => (this.card = node);
    this.captureOverlay = (node) => (this.overlay = node);
    this.captureInput = (node) => (this.input = node);
    this.captureAccept = (node) => (this.accept = node);
    this.bindMethods('present');
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life || !this.context) return;
    servicesContext(this.context, ['a11y', 'dialogs']);
    const controller = this.controller || this.context.services.dialogs;
    if (this.boundConnection !== life || this.boundController !== controller) {
      this.releasePresenter?.();
      this.boundConnection = life;
      this.boundController = controller;
      const release = controller.attach(this.present);
      this.releasePresenter = release;
      life.own(() => {
        release();
        if (this.releasePresenter === release) this.releasePresenter = null;
      });
    }
    if (this.request && this.card && !this.modal) {
      const request = this.request;
      const modal = this.context.services.a11y.openModal({
        overlay: this.overlay,
        card: this.card,
        initialFocus: this.request.kind === 'prompt' ? this.input : this.accept,
        closeOnOverlayClick: false,
        signal: life.signal,
        onClose: () => request.finish()
      });
      this.modal = modal;
      this.releaseModal = life.own(() => {
        modal.destroy();
        if (this.modal === modal) this.modal = null;
      });
    }
  }

  present(request) {
    const modal = this.modal;
    this.releaseModal?.();
    this.releaseModal = null;
    this.modal = null;
    this.request = request;
    this.selectedInput = false;
    modal?.destroy();
    return request ? this.whenRendered() : undefined;
  }

  stopClick(event) {
    event.stopPropagation();
  }

  submit(request, event) {
    event.preventDefault();
    if (request !== this.request) return;
    request.onConfirm?.();
    return request.finish(
      request.kind === 'prompt' ? this.input.value : request.kind === 'confirm' ? true : undefined
    );
  }

  selectInput() {
    if (this.selectedInput) return;
    this.selectedInput = true;
    this.input.select();
  }

  render() {
    const request = this.request;
    if (!request) return null;
    const { id, kind, message } = request;
    return this.portal(
      'decision',
      'body',
      <>
        {[
          <div key={id} ref={this.captureOverlay} class="modal-overlay app-dialog-overlay" data-dialog-kind={kind} onClick={this.stopClick}>
            <form ref={this.captureCard} class="modal-card app-dialog-card" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-message`} tabindex="-1" onSubmit={(event) => this.submit(request, event)}>
              <h2 id={`${id}-title`}>
                {kind === 'confirm' ? 'Confirm' : kind === 'prompt' ? 'Enter a value' : 'Notice'}
              </h2>
              <p id={`${id}-message`} class="app-dialog-message">{message}</p>
              <div hidden={kind !== 'prompt'}>
                <input
                  ref={this.captureInput}
                  type="text"
                  class="input"
                  aria-labelledby={`${id}-message`}
                  bind={bindControl(request.value, null, { mode: 'browser', session: request })}
                  onFocus={this.selectInput}/>
              </div>
              <footer>
                <button
                  type="button"
                  class="button-secondary"
                  hidden={kind === 'alert'}
                  onClick={() => request.finish()}>
                  Cancel
                </button>
                <button ref={this.captureAccept} type="submit" class="button-primary">OK</button>
              </footer>
            </form>
          </div>
        ]}
      </>
    );
  }
}

export function registerComponents(registry) {
  defineUIElements(registry, { 'tbc-dialog-layer': DialogLayer });
}
